import { OButton, OPopper, OTooltip } from '@src/shared';
import useActionsMenu from './useParticipantsActionsMenu';
import { IoIosArrowDown } from 'react-icons/io';
import { EUserType, IPeer } from '@src/types';
import { usePeersStore } from '@src/stores';
import { useShallow } from 'zustand/react/shallow';

interface IProps {
  participant: IPeer;
  iconButton?: React.ReactNode;
  onHideSheet?: () => void;
}

const ActionsMenu = ({ participant, iconButton, onHideSheet }: IProps) => {
  const {
    isVideoPinned,
    isMenuPopperOpened,
    isParticipantSharingScreen,
    isPrivateChatEnabled,
    isPublicChatEnabled,
    canUserAskToUnmute,
    canUserEject,
    canUserBan,
    handlePinVideo,
    handleOpenChat,
    setIsMenuPopperOpened,
    handleCoHost,
    handleParticipantAction,
    isTargetHost,
    handleBanParticipant,
  } = useActionsMenu({ participant });

  const { isHostUser } = usePeersStore(
    useShallow((state) => ({
      isHostUser: state.isHostUser,
    }))
  );

  const peerType = participant.peer_info.user_type;

  return (
    <OPopper
      icon={iconButton ? iconButton : <OButton text="More" size="small" icon={<IoIosArrowDown />} onClick={() => {}} />}
      classes={{
        iconButton: 'options-button',
      }}
      onClose={() => setIsMenuPopperOpened(false)}
      onOpen={() => setIsMenuPopperOpened(true)}
      isActive={isMenuPopperOpened}
    >
      <div className="action-menu">
        {/* Chat */}
        {isPrivateChatEnabled && isPublicChatEnabled && (
          <div
            className="menu-item"
            onClick={() => {
              handleOpenChat();
              onHideSheet?.();
            }}
          >
            Chat
          </div>
        )}

        {/* Pin/Unpin Video */}
        <div
          className="menu-item"
          onClick={() => {
            handlePinVideo();
            onHideSheet?.();
          }}
        >
          {isVideoPinned ? 'Unpin' : 'Pin'}
        </div>

        {/* Assign/Remove Co-host (Host only, cannot assign to self) */}
        {isHostUser && !isTargetHost && (
          <div className="menu-item" onClick={() => handleCoHost()}>
            {peerType === EUserType.CO_HOST ? `Remove as co-host` : `Assign as a co-host`}
          </div>
        )}

        {/* Ask to open camera */}
        {canUserAskToUnmute && (
          <div
            className={`menu-item ${participant.peer_info.peer_video ? 'disabled-item' : ''}`}
            onClick={() => !participant.peer_info.peer_video && handleParticipantAction('requestOpenCamera')}
          >
            Ask to open camera
          </div>
        )}

        {/* Ask to open mic */}
        {canUserAskToUnmute && (
          <div
            className={`menu-item ${participant.peer_info.peer_audio ? 'disabled-item' : ''}`}
            onClick={() => !participant.peer_info.peer_audio && handleParticipantAction('requestToUnmute')}
          >
            Ask to open mic
          </div>
        )}

        {/* Remove participant */}
        {canUserEject && (
          <div className="menu-item" onClick={() => handleParticipantAction('eject')}>
            Remove
          </div>
        )}

        {/* Ban participant */}
        {canUserBan && (
          <div className="menu-item" onClick={() => handleBanParticipant()}>
            Ban
          </div>
        )}

        {/* Coming Soon items */}
        <OTooltip title="Coming Soon">
          <div className="menu-item disabled-item">Share video</div>
        </OTooltip>
        <OTooltip title="Coming Soon">
          <div
            className="menu-item disabled-item"
            onClick={() => handleParticipantAction(isParticipantSharingScreen ? 'stop' : 'start')}
          >
            {`${isParticipantSharingScreen ? 'Stop' : 'Start'} screen share`}
          </div>
        </OTooltip>
      </div>
    </OPopper>
  );
};

export default ActionsMenu;
